/**
 * The keystroke fast path (principle 4; table F in the part 10 log): typing a letter, digit or space between two
 * characters of text, or deleting one character of text from between two others, leaves a canonical chapter canonical
 * as it is, so it needs no rewrite. A property checks that every edit this lets through gives exactly what `rewrite`
 * gives (F7).
 *
 * Why it holds: the edit's neighbours are text on the same line and no delimiter character, so no delimiter's neighbours
 * change (flanking and run length, L6), no
 * line edge or line start changes (E13–E18, E20), and nothing lands in a mark, value or break (P2–P6). The one escape
 * whose need depends on its neighbours is a list marker's delimiter after a line's leading digits (E19), so a point in
 * those digits or right after their delimiter is left to `rewrite`.
 */
import type { ParsedChapter } from '@cockatrice/core';
import type { SyntaxNode } from '@lezer/common';
import { leafAt } from './structure.ts';

/** F1: what may be typed or deleted: one letter, digit, combining mark or space. */
const PLAIN = /^[\p{L}\p{N}\p{M} ]$/u;

/** F6: punctuation the stored form escapes wherever it stands (part 04, E1–E8). */
const ALWAYS_ESCAPED = /^[\\*_^[<&`]$/u;

/**
 * F6: punctuation the stored form writes as it is between two characters of text, away from a line's start and end:
 * each escape rule for these looks at a line start (E13–E19), the end of a heading (E22), a link's text (E9), or a
 * neighbour that is markup (E10–E12), none of which a point between text inside a leaf's content can be.
 */
const NEVER_ESCAPED = /^[.,;:!?'"()/\-+=~>#%@$|{}]$/u;

/**
 * What typing `insert` at a point the fast path allows stores: the character, escaped when it is always escaped (F6);
 * null when it isn't one the fast path takes.
 */
export function fastText(insert: string): string | null {
  if (PLAIN.test(insert) || NEVER_ESCAPED.test(insert)) return insert;
  return ALWAYS_ESCAPED.test(insert) ? `\\${insert}` : null;
}

/** Leaf blocks: their content is the writer's text. */
const LEAF = /^(?:Paragraph|ATXHeading\d|SetextHeading\d|CenteredBlock)$/u;

/** Nodes that hold text as text: an escape or entity is one character of text, whole. */
const TEXT_NODES: ReadonlySet<string> = new Set(['Escape', 'Entity']);

/** Inline nodes whose own characters are all marks or children: plain text inside them is text. */
const CONTENT_NODES: ReadonlySet<string> = new Set(['Emphasis', 'StrongEmphasis', 'SmallCaps', 'Link', 'Image']);

/** Whether the node a side of `pos` reaches is text: inside a leaf, never a mark, a value or a break (F2). */
function textAt(parsed: ParsedChapter, pos: number, side: -1 | 1): boolean {
  const char = parsed.text.charAt(side < 0 ? pos - 1 : pos);
  // A point between a surrogate pair's halves is inside one character (P7).
  if (char === '' || char === '\n' || /[\uDC00-\uDFFF]/u.test(parsed.text.charAt(pos))) return false;
  const node: SyntaxNode = parsed.tree.resolveInner(pos, side);
  // An escape or entity is text when the point is at its edge, never inside it (F3).
  if (TEXT_NODES.has(node.name)) return side < 0 ? node.to === pos : node.from === pos;
  // A delimiter character written as it is (the unmatched part of a run, U1) pairs by its neighbours (L6).
  if (char === '*' || char === '_' || char === '^') return false;
  // Plain text sits directly in a leaf, or in an inline element's content; a leaf's own markup (a heading's `#` run and
  // the blank after it, a centered block's marks, a container prefix after a soft break) lies at a line's start or end,
  // which the content and line checks rule out.
  return LEAF.test(node.name) || CONTENT_NODES.has(node.name);
}

/**
 * F4: whether `pos` is in its line's leading digits, or right after them and a `.` or `)`, counted from where the line's
 * content starts (after the leaf's own mark on its first line, after the container prefix on later ones): a character
 * typed or removed there could decide whether the `.` or `)` reads as a list marker (E19).
 */
function inLeadingDigits(text: string, contentFrom: number, pos: number): boolean {
  const lineStart = Math.max(contentFrom, text.lastIndexOf('\n', pos - 1) + 1);
  // The delimiter may be written escaped (`1945\.`), when the canonical form needed it.
  return /^[ \t>]*\d*(?:\\?[.)])?$/u.test(text.slice(lineStart, pos));
}

/**
 * What replacing `from`–`to` with `insert` stores, when the fast path allows the edit (table F): the text to put in
 * its place; or null for an edit `rewrite` must make.
 */
export function fastWrite(parsed: ParsedChapter, from: number, to: number, insert: string): string | null {
  const { text } = parsed;
  // F4: strictly inside a leaf's content, never at a line's content start or in its leading digits.
  const leaf = leafAt(parsed, from);
  if (leaf === null || from <= leaf.contentFrom || to >= leaf.contentTo || inLeadingDigits(text, leaf.contentFrom, from)) return null;
  if (emptyAfter(text, leaf.contentFrom, leaf.contentTo, from, to)) return null;
  if (from === to) {
    const written = fastText(insert);
    return written !== null && textAt(parsed, from, -1) && textAt(parsed, from, 1) && !inLinkText(parsed, from) ? written : null;
  }
  // F5: one character removed, with text on both sides of it.
  return insert === '' && PLAIN.test(text.slice(from, to)) && textAt(parsed, from, -1) && textAt(parsed, to, 1) && textAt(parsed, from, 1) ? '' : null;
}

/**
 * F8 (part 10b): whether the leaf's content around the edit is no-break spaces alone, as the character or `&nbsp;`: an
 * empty paragraph, whose stored form `rewrite` writes (S1) and whose text typed in replaces it (T1). Reads the leaf only
 * when both neighbours of the edit can be one.
 */
function emptyAfter(text: string, contentFrom: number, contentTo: number, from: number, to: number): boolean {
  if (!/[\u00a0;]/u.test(text.charAt(from - 1)) || !/[\u00a0&]/u.test(text.charAt(to))) return false;
  return /^(?:\u00a0|&nbsp;)+$/u.test(text.slice(contentFrom, from) + text.slice(to, contentTo));
}

/** Whether `pos` lies in a link's or image's text, where `]` and `!` follow rules of their own (E9, E10). */
function inLinkText(parsed: ParsedChapter, pos: number): boolean {
  for (let node: SyntaxNode | null = parsed.tree.resolveInner(pos, -1); node !== null; node = node.parent) {
    if (node.name === 'Link' || node.name === 'Image') return true;
  }
  return false;
}
